import React, { useState, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { EyeIcon, TrashIcon } from "@heroicons/react/outline";
import styled from "styled-components"; // Keep styled for RequestList UL

import { TitleM } from "../../../components/atoms/Typography";
import { Tooltip } from "../../../components/atoms/Tooltip";
import { Badge } from "../../../components/atoms/Badge";
import { Breadcrumb } from "../../../components/molecules/Breadcrumb";
import { Theme } from "../../../components/atoms/theme";
import { ContentWrapper } from "../../../components/ContentWrapper";
import { useGetPatientsQuery } from "../../../redux/reduxQuery/patientApi";
import { useGetRequestsQuery } from "../../../redux/reduxQuery/requestApi"; // Corrected import
import { EyeTrashIconButtons } from "../../../components/molecules/TableActionButtons";
import { CustomTable } from "../../../components/organisms/ShimmerTable";
import { formatDate } from "../../../utils/formatDate";
import {
  useDeleteFirmMutation,
  useGetFirmQuery,
} from "../../../redux/reduxQuery/firmApi";
import { getRequestsFromRequests } from "../../../common/request"; // Corrected import
import { Status } from "../Requests";
import { RenderCell } from "../../../common/fv";
import { Button, Modal } from "@tyrannosaurustech/ui";
import { Tabs, Tab } from "../../../components/molecules/Tabs";
import Users from "../../Common/Users";
import EditGeneralInfo from "./EditGeneralInfo"; // New import
import { Loading } from "../../../RouteNavigator";

const UL = styled.ul`
  margin: 0;
  padding-left: 0.938rem;
`;
const RequestList = ({ requests }) => (
  <UL>
    {requests.map((request) => (
      <li key={request}>{request}</li>
    ))}
  </UL>
);

export default function EditLawFirm() {
  let navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const firmId = id as string;
  const [deleteLawFirmModal, setDeleteLawFirmModal] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [openBulkAddUsers, setOpenBulkAddUsers] = useState(
    (location.state as { openBulkAddUsers?: boolean })?.openBulkAddUsers ||
      false
  );
  const [deleteFirm] = useDeleteFirmMutation();
  const { data: patientData, isFetching: isFetchingPatient } =
    useGetPatientsQuery(`firmId=${firmId}`, {
      refetchOnMountOrArgChange: true,
    });
  const { data: requestData, isFetching: isFetchingRequest } =
    useGetRequestsQuery(`firmId=${firmId}`, {
      refetchOnMountOrArgChange: true,
    });
  const { data: firmData, isFetching: isFetchingFirm } = useGetFirmQuery(
    firmId,
    { refetchOnMountOrArgChange: true }
  );

  const onDelete = async (data) => {
    setButtonLoader(true);
    await deleteFirm(data?.id);
    setButtonLoader(false);
    setDeleteLawFirmModal(false);
    navigate("/admin/law-firms");
  };

  const crumbLinks = [
    {
      text: "Law Firms",
      path: "/admin/law-firms",
    },
    {
      text: firmData?.name,
    },
  ];

  const buttons = [
    <Button
      icon="TrashIcon"
      onClick={() => setDeleteLawFirmModal(true)}
      label="Delete Law Firm"
      base="secondary"
      size="small"
    />,
  ];

  const patientColumns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Birth Date",
        accessor: "dateOfBirth",
        Cell: (cell) => {
          const { dateOfBirth } = cell.row.values;
          return formatDate(dateOfBirth);
        },
      },
      {
        Header: "SSN",
        accessor: "ssn",
      },
      {
        Header: "Law Firm",
        accessor: "firm.name",
        includeToolTip: true,
      },
      {
        Header: "FV Status",
        Cell: (cellProps) => RenderCell(cellProps),
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: (cell) => {
          const patientId = cell.row.values.id;
          return (
            <EyeTrashIconButtons
              link={`/admin/patients/${patientId}`}
              icons={[EyeIcon, TrashIcon]}
              onClick1={() => navigate(`/admin/patients/${patientId}`)}
              onClick2={() => {
                //setIsOpen(true)
              }}
            />
          );
        },
      },
    ],
    [navigate]
  );

  const requestColumns = useMemo(
    () => [
      {
        Header: "Law Firm",
        accessor: "firm.name",
        includeToolTip: true,
      },
      {
        Header: "Patient",
        accessor: "patient.name",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (cell) => <Status status={cell.value} />,
      },
      {
        Header: "Requests",
        accessor: (row) => getRequestsFromRequests(row).length,
        Cell: (cellProps) => {
          const request = cellProps.row.original;
          const requests = getRequestsFromRequests(request);
          return (
            <Tooltip
              text={<RequestList requests={requests} />}
              position="top"
              background={Theme.colors.neutrals_000}
            >
              <Badge
                icon={true}
                color={Theme.colors.primaries_000}
                hoverColor={Theme.colors.primaries_neg100}
                badgeText={`${requests.length}`}
                img={
                  <EyeIcon
                    width={"0.938rem"}
                    color={Theme.colors.neutrals_500}
                  />
                }
              />
            </Tooltip>
          );
        },
      },
      {
        Header: "Medical Provider",
        accessor: "provider.name",
        includeToolTip: true,
      },
      {
        Header: "Service Dates",
        accessor: "serviceDates",
      },
      {
        Header: "Submitted",
        accessor: (row) => formatDate(row.submittedDate),
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: (cell) => {
          const requestId = cell.row.values.id;
          return (
            <EyeTrashIconButtons
              icons={[EyeIcon, TrashIcon]}
              link={`/admin/requests/${requestId}`}
              onClick1={() => navigate(`/admin/requests/${requestId}`)}
              onClick2={() => {
                //setIsOpen(true)
              }}
            />
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <ContentWrapper height={"100%"}>
      {isFetchingFirm ? (
        <Loading />
      ) : (
        <>
          <div style={{ marginBottom: "1.5rem" }} />
          <Breadcrumb crumbs={crumbLinks} buttons={buttons} />
          <TitleM>{firmData?.name}</TitleM>
          <Tabs defaultTab={openBulkAddUsers ? "Users" : "General Info"}>
            <Tab label="General Info">
              <EditGeneralInfo firmData={firmData} />
            </Tab>
            <Tab label="Users">
              <Users
                firmId={firmData?.id}
                openBulkAddUsers={openBulkAddUsers}
                onCloseBulkAddUsers={() => setOpenBulkAddUsers(false)}
              />
            </Tab>
            <Tab label="Requests">
              <CustomTable
                tableType="request"
                columns={requestColumns}
                data={requestData?.requests || []}
                isLoading={isFetchingRequest}
                navigation={() =>
                  navigate("/admin/requests/new", {
                    state: {
                      defaultValues: {
                        firmId: id,
                        firmName: firmData?.name,
                      },
                      cameFrom: location.pathname,
                    },
                  })
                }
              />
            </Tab>
            <Tab label="Patients">
              <CustomTable
                tableType="patients"
                columns={patientColumns}
                data={patientData || []}
                isLoading={isFetchingPatient}
                navigation={() =>
                  navigate("new-patient", {
                    state: { cameFrom: location.pathname },
                  })
                }
              />
            </Tab>
          </Tabs>
        </>
      )}
      <Modal
        title="Delete Law Firm"
        bodyText={`Are you sure you want to delete ${firmData?.name}? If you do you won’t be able to recover it.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={buttonLoader}
        onConfirm={() => onDelete(firmData)}
        setOpen={setDeleteLawFirmModal}
        open={deleteLawFirmModal}
        variant="danger"
      />
    </ContentWrapper>
  );
}
